import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar as CalendarIcon, MapPin, Users, Plus } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function EventCalendar() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [servers, setServers] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const fetchEvents = async () => {
      try {
        const token = localStorage.getItem('token');
        const serversRes = await axios.get(`${API}/api/servers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });

        const events = [];
        for (const server of serversRes.data) {
          const eventsRes = await axios.get(`${API}/api/servers/${server.server_id}/events`);
          events.push(...eventsRes.data.map(e => ({...e, server_name: server.name})));
        }

        setAllEvents(events.sort((a, b) => new Date(a.event_date) - new Date(b.event_date)));
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [user, navigate]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <Button
                onClick={() => navigate('/home')}
                variant="ghost"
                className="mr-4 border-2 border-black rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Events Calendar</h1>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {allEvents.length === 0 ? (
          <div className="text-center py-16 border-4 border-dashed border-slate-300 rounded-3xl">
            <div className="w-24 h-24 bg-slate-100 rounded-full mx-auto mb-6 flex items-center justify-center border-2 border-black">
              <CalendarIcon className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>No upcoming events</h3>
            <p className="text-slate-600">Join servers to see their events</p>
          </div>
        ) : (
          <div className="space-y-6">
            {allEvents.map(event => (
              <div
                key={event.event_id}
                className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal hover:shadow-brutal-lg transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <Badge className="mb-2 bg-cyan-100 text-slate-900 border-2 border-black">
                      {event.server_name}
                    </Badge>
                    <h3 className="text-2xl font-black text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
                      {event.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{event.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {formatDate(event.event_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {event.location.town}, {event.location.state}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {event.attendee_count} attending
                        {event.max_attendees && ` / ${event.max_attendees} max`}
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/events/${event.event_id}`)}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold"
                  >
                    View Event
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}